import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { LocaleLink as Link } from '../lib/locale-link.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { MarketingShell } from '../components/marketing-shell.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { logger } from '../lib/logger.js';
import {
  isHelpSlug,
  loadHelpArticle,
  helpToc,
  type HelpMdxComponents,
  type HelpSlug,
  type HelpTocEntry,
  type LoadedHelpArticle,
} from '../features/help/articles.js';
import { HelpFeedbackWidget } from '../features/help/feedback.js';
import { useHelpStructuredData } from '../features/help/structured-data.js';
import type { Locale } from '@kilat/shared';

/**
 * /help + /help/{slug} — the operator manual (slice 3 completes it: all 14
 * HELP_SLUGS articles × en/ms/zh/ta, plus the per-article "Was this helpful?"
 * widget writing to helpFeedback/ — see features/help/feedback.tsx).
 *
 * Locale is derived from the URL prefix (SEO slice 2b — ADR-0024): `/help` is
 * en, `/ms/help/{slug}` is Bahasa Malaysia, etc. Articles load the active
 * locale's MDX with an explicit English fallback: the reader is TOLD they're
 * seeing English rather than silently swapped (no-dead-ends).
 *
 * `useMarketingSeo` emits the per-locale `<title>`, canonical, and hreflang
 * alternates for each of these routes from the current path.
 */

type LoadPhase<T> = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; data: T };

function formatUpdatedAt(isoDate: string, locale: Locale): string {
  // Display-only formatting of the article's authored date constant
  // (content meta, not a stored Firestore value).
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(parsed);
}

// ── /help — table of contents ───────────────────────────────────────────

export function HelpIndexPage() {
  const brand = usePlatformBrand();
  const { t, locale } = useI18n();
  const [phase, setPhase] = useState<LoadPhase<readonly HelpTocEntry[]>>({ kind: 'loading' });
  const [reloadTick, setReloadTick] = useState(0);

  useMarketingSeo({
    title: `${t('marketing.help.title')} — ${brand.name}`,
    description: t('marketing.help.subtitle', { brand: brand.shortName }),
  });

  useEffect(() => {
    // Synchronous: article metadata is inlined at build time (see articles.ts),
    // so the index has nothing to wait for. The error branch stays because a
    // missing article should say so rather than render an empty manual.
    try {
      setPhase({ kind: 'ready', data: helpToc(locale) });
    } catch (e: unknown) {
      logger.error('help toc load failed', { locale, error: String(e) });
      setPhase({ kind: 'error' });
    }
  }, [locale, reloadTick]);

  return (
    <MarketingShell>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--space-12) var(--space-6)' }}>
        <header>
          <p style={eyebrowStyle}>{t('marketing.help.eyebrow')}</p>
          <h1 style={pageH1Style}>{t('marketing.help.title')}</h1>
          <p style={pageSubStyle}>{t('marketing.help.subtitle', { brand: brand.shortName })}</p>
        </header>

        <div style={{ marginTop: 'var(--space-8)' }}>
          {phase.kind === 'loading' ? (
            <p style={quietStateStyle}>{t('marketing.help.tocLoading')}</p>
          ) : null}
          {phase.kind === 'error' ? (
            <div style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'start' }}>
              <p style={quietStateStyle}>{t('marketing.help.tocError')}</p>
              <button
                type="button"
                onClick={() => setReloadTick((n) => n + 1)}
                style={retryButtonStyle}
              >
                {t('marketing.help.retry')}
              </button>
            </div>
          ) : null}
          {phase.kind === 'ready' ? (
            <>
              <nav style={{ display: 'grid', gap: 'var(--space-3)' }}>
                {phase.data.map((entry) => (
                  <TocCard key={entry.slug} entry={entry} locale={locale} />
                ))}
              </nav>
              <p
                style={{
                  margin: 'var(--space-6) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  lineHeight: 1.5,
                }}
              >
                {t('marketing.help.moreComing')}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </MarketingShell>
  );
}

function TocCard({ entry, locale }: { readonly entry: HelpTocEntry; readonly locale: Locale }) {
  const { t } = useI18n();
  return (
    <Link
      to={`/help/${entry.slug}`}
      style={{
        display: 'block',
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
        boxShadow: 'var(--shadow-1)',
        textDecoration: 'none',
        color: 'var(--color-on-surface)',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 'var(--text-body)', fontWeight: 600, lineHeight: 1.35 }}>
          {entry.meta.title}
        </span>
        {entry.localeUsed !== locale ? (
          <span style={englishBadgeStyle}>{t('marketing.help.inEnglishBadge')}</span>
        ) : null}
      </span>
      <span
        style={{
          display: 'block',
          marginTop: 'var(--space-2)',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t('marketing.help.lastUpdated', {
          date: formatUpdatedAt(entry.meta.updatedAt, locale),
        })}
      </span>
    </Link>
  );
}

// ── /help/{slug} — a single article ─────────────────────────────────────

export function HelpArticlePage() {
  const { slug: rawSlug } = useParams();
  const slug = rawSlug && isHelpSlug(rawSlug) ? rawSlug : null;

  return (
    <MarketingShell>{slug ? <ArticleBody slug={slug} /> : <ArticleNotFound />}</MarketingShell>
  );
}

function ArticleBody({ slug }: { readonly slug: HelpSlug }) {
  const brand = usePlatformBrand();
  const { t, locale } = useI18n();
  const [phase, setPhase] = useState<LoadPhase<LoadedHelpArticle>>({ kind: 'loading' });
  const [related, setRelated] = useState<readonly HelpTocEntry[]>([]);
  const [reloadTick, setReloadTick] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  useMarketingSeo({
    title: phase.kind === 'ready' ? `${phase.data.meta.title} — ${brand.name}` : null,
  });

  // Schema.org JSON-LD (TechArticle for every article, FAQPage for the faq
  // article's Q&A) — injected once the body renders so it lands in the static
  // prerender snapshot (ADR-0024 slice 2b part B).
  useHelpStructuredData({
    slug,
    ready: phase.kind === 'ready',
    title: phase.kind === 'ready' ? phase.data.meta.title : null,
    updatedAt: phase.kind === 'ready' ? phase.data.meta.updatedAt : null,
    localeUsed: phase.kind === 'ready' ? phase.data.localeUsed : null,
    brandName: brand.name,
    bodyRef,
  });

  useEffect(() => {
    let cancelled = false;
    setPhase({ kind: 'loading' });
    setRelated([]);
    loadHelpArticle(locale, slug)
      .then((article) => {
        if (cancelled) return;
        setPhase({ kind: 'ready', data: article });
        // Related titles come from the sibling articles' own meta, which is
        // inlined — reading them no longer downloads the siblings themselves.
        setRelated(helpToc(locale).filter((e) => article.meta.related.includes(e.slug)));
      })
      .catch((e: unknown) => {
        logger.error('help article load failed', { slug, locale, error: String(e) });
        if (!cancelled) setPhase({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [locale, slug, reloadTick]);

  return (
    <article style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--space-12) var(--space-6)' }}>
      <Link
        to="/help"
        style={{
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          textDecoration: 'none',
        }}
      >
        {t('marketing.help.backToHelp')}
      </Link>

      {phase.kind === 'loading' ? (
        <p style={{ ...quietStateStyle, marginTop: 'var(--space-8)' }}>
          {t('marketing.help.articleLoading')}
        </p>
      ) : null}

      {phase.kind === 'error' ? (
        <div
          style={{
            marginTop: 'var(--space-8)',
            display: 'grid',
            gap: 'var(--space-3)',
            justifyItems: 'start',
          }}
        >
          <p style={quietStateStyle}>{t('marketing.help.articleError')}</p>
          <button
            type="button"
            onClick={() => setReloadTick((n) => n + 1)}
            style={retryButtonStyle}
          >
            {t('marketing.help.retry')}
          </button>
        </div>
      ) : null}

      {phase.kind === 'ready' ? (
        <>
          <header style={{ marginTop: 'var(--space-6)' }}>
            <p style={eyebrowStyle}>{t('marketing.help.eyebrow')}</p>
            <h1 style={pageH1Style}>{phase.data.meta.title}</h1>
            <p
              style={{
                margin: 'var(--space-3) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {t('marketing.help.lastUpdated', {
                date: formatUpdatedAt(phase.data.meta.updatedAt, locale),
              })}
            </p>
            {phase.data.localeUsed !== locale ? (
              <p
                style={{
                  margin: 'var(--space-4) 0 0 0',
                  padding: 'var(--space-3) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  background: 'rgba(245,158,11,0.10)',
                  border: '1px solid rgba(245,158,11,0.35)',
                  fontSize: 'var(--text-meta)',
                  lineHeight: 1.5,
                }}
              >
                {t('marketing.help.englishFallbackNote')}
              </p>
            ) : null}
          </header>

          <div ref={bodyRef} style={{ marginTop: 'var(--space-6)' }}>
            <phase.data.Body components={MDX_COMPONENTS} />
          </div>

          <HelpFeedbackWidget key={slug} slug={slug} />

          {related.length > 0 ? (
            <footer
              style={{
                marginTop: 'var(--space-10)',
                paddingTop: 'var(--space-6)',
                borderTop: '1px solid var(--color-border)',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 'var(--text-h2)',
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {t('marketing.help.relatedHeading')}
              </h2>
              <nav
                style={{
                  marginTop: 'var(--space-4)',
                  display: 'grid',
                  gap: 'var(--space-3)',
                }}
              >
                {related.map((entry) => (
                  <TocCard key={entry.slug} entry={entry} locale={locale} />
                ))}
              </nav>
            </footer>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

function ArticleNotFound() {
  const { t } = useI18n();
  const brand = usePlatformBrand();
  useMarketingSeo({
    title: `${t('marketing.help.notFoundTitle')} — ${brand.name}`,
    description: t('marketing.help.notFoundBody'),
  });
  return (
    <div
      style={{
        maxWidth: 560,
        margin: '0 auto',
        padding: 'var(--space-16) var(--space-6)',
        textAlign: 'center',
      }}
    >
      <p style={eyebrowStyle}>{t('marketing.help.eyebrow')}</p>
      <h1 style={pageH1Style}>{t('marketing.help.notFoundTitle')}</h1>
      <p style={pageSubStyle}>{t('marketing.help.notFoundBody')}</p>
      <div style={{ marginTop: 'var(--space-6)' }}>
        <Link
          to="/help"
          style={{
            display: 'inline-block',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            padding: '0.75em 1.5em',
            borderRadius: 'var(--radius-pill)',
            fontWeight: 600,
            textDecoration: 'none',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {t('marketing.help.notFoundCta')}
        </Link>
      </div>
    </div>
  );
}

// ── MDX article typography (marketing tokens, mirrors legal.tsx scale) ──

const MDX_COMPONENTS: HelpMdxComponents = {
  h2: ({ children }) => (
    <h2
      style={{
        margin: 'var(--space-8) 0 0 0',
        fontSize: 'var(--text-h2)',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      }}
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      style={{
        margin: 'var(--space-6) 0 0 0',
        fontSize: 'var(--text-body)',
        fontWeight: 600,
        lineHeight: 1.35,
      }}
    >
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p
      style={{
        margin: 'var(--space-4) 0 0 0',
        fontSize: 'var(--text-body)',
        lineHeight: 1.7,
        color: 'var(--color-on-surface)',
      }}
    >
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul
      style={{
        margin: 'var(--space-4) 0 0 0',
        paddingLeft: 'var(--space-6)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol
      style={{
        margin: 'var(--space-4) 0 0 0',
        paddingLeft: 'var(--space-6)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li style={{ fontSize: 'var(--text-body)', lineHeight: 1.6 }}>{children}</li>
  ),
  a: ({ href, children }) => (
    <a href={href} style={{ color: 'var(--color-primary)', textDecorationThickness: '1px' }}>
      {children}
    </a>
  ),
};

// ── shared styles ───────────────────────────────────────────────────────

const eyebrowStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-primary)',
  letterSpacing: 3,
  textTransform: 'uppercase',
  fontWeight: 600,
  margin: 0,
};

const pageH1Style: React.CSSProperties = {
  margin: 'var(--space-2) 0 0 0',
  fontSize: 'var(--text-h1)',
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
};

const pageSubStyle: React.CSSProperties = {
  margin: 'var(--space-3) 0 0 0',
  fontSize: 'var(--text-body)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const quietStateStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const retryButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface)',
  padding: '0.5em 1.25em',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
};

const englishBadgeStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-pill)',
  padding: '0.1em 0.75em',
  whiteSpace: 'nowrap',
};
